const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === "true";

class AnalyticsTracker {
  constructor() {
    this.sessionId = this._generateSessionId();
    this.sessionStartTime = Date.now();
    this.currentPage = null;
    this.pageStartTime = null;
    this.userProfile = null;
    this.pendingEvents = [];
    this.flushInterval = null;

    if (ANALYTICS_ENABLED) {
      this._startAutoFlush();
      window.addEventListener("pagehide", () => { this.flush(); });
    }
  }

  _generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  _startAutoFlush() {
    // Flush events every 10 seconds
    this.flushInterval = setInterval(() => {
      this.flush();
    }, 10000);
  }

  setUser(profile) {
    this.userProfile = profile;
  }

  trackPageView(pageName) {
    // Skip tracking for the analytics page itself
    if (pageName === 'analytics') {
      return;
    }

    // Track duration of previous page
    if (this.currentPage && this.pageStartTime) {
      this.trackEvent("page_duration", {
        page: this.currentPage,
        duration: Date.now() - this.pageStartTime,
      });
    }

    // Set new page
    this.currentPage = pageName;
    this.pageStartTime = Date.now();

    this.trackEvent("page_view", {
      page: pageName,
    });
  }

  trackEvent(eventType, eventData = {}) {
    if (!ANALYTICS_ENABLED) return;

    // Skip tracking if user is not authenticated
    if (!this.userProfile) {
      return;
    }

    const userEmail = this.userProfile?.preferred_username || this.userProfile?.email || "anonymous";

    const event = {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      eventType,
      eventData,
      page: this.currentPage,
      userEmail: userEmail,
      userName: this.userProfile?.name || this.userProfile?.given_name || "unknown",
      userAgent: navigator.userAgent,
      url: `${window.location.origin}${window.location.pathname}`,
    };

    this.pendingEvents.push(event);

    // Auto-flush if we have too many pending events
    if (this.pendingEvents.length >= 20) {
      this.flush();
    }
  }

  trackAction(actionName, details = {}) {
    this.trackEvent("user_action", {
      action: actionName,
      ...details,
    });
  }

  trackApiCall(endpoint, method, duration, success) {
    this.trackEvent("api_call", {
      endpoint,
      method,
      duration,
      success,
    });
  }

  trackError(error, context = {}) {
    this.trackEvent("error", {
      message: error?.message || String(error),
      context,
    });
  }

  async flush() {
    if (this.pendingEvents.length === 0) return;

    const eventsToSend = [...this.pendingEvents];
    this.pendingEvents = [];

    try {
      const response = await fetch(`${API_BASE}/analytics/events`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ events: eventsToSend }),
      });
      if (!response.ok) throw new Error(`Analytics upload failed (${response.status})`);
    } catch (error) {
      console.error("Failed to send analytics events:", error);
    }
  }

  getSessionDuration() {
    return Date.now() - this.sessionStartTime;
  }

  cleanup() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush();
  }
}

// Create singleton instance
const analytics = new AnalyticsTracker();

export default analytics;
